/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { Navigation } from "@/components/customCopmonent/landingSection/navigation";
import { FooterSection } from "@/components/customCopmonent/landingSection/footer-section";
import DotGrid from "@/components/customCopmonent/DotGrid";
import { PageHeaderHero, PAGE_HERO_CLASS } from "@/components/customCopmonent/Text/PageHeaderHero";
import { SectionWrapper } from "../Clientele.page-primitives";
import { RevealOnScroll } from "@/components/SpecialProjectsPage.primitives";

import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom"; // âœ… NEW
import { useTranslation } from "react-i18next";
import ProductCard from "@/components/customCopmonent/ProductCard";
import Seo from "@/components/Seo";
import AcheShowcase, {
	type ShowcaseHighlight,
	type ShowcaseSpec,
} from "@/components/customCopmonent/AcheShowcase";
import {
	ShieldCheck,
	Shapes,
	Diamond,
	Gauge,
	Thermometer,
	Grid3x3,
	Layers,
	Workflow,
} from "lucide-react";

// -----------------------------
// âœ… NEW: Industry Config
// -----------------------------
const INDUSTRIES = ["Oil & Gas", "Marine & Offshore", "Water Treatment"];

// slug â†’ label mapping
const INDUSTRY_SLUG_MAP: Record<string, string> = {
	"power-generation": "Power Generation",
	"oil-gas": "Oil & Gas",
	"marine-offshore": "Marine & Offshore",
	"water-treatment": "Water Treatment",
	"plastic-injection": "Plastic Injection Molding & Machine Tools",
	hvac: "HVAC Systems",
	"chemical-processing": "Chemical Processing",
	"data-center": "Data Center",
	"steel-metallurgy": "Steel & Metallurgy",
	"renewable-energy": "Renewable Energy",
	"process-industries": "Process Industries (Pulp, Paper & Sugar)",
};

// reverse mapping
const getSlugFromIndustry = (industry: string) => {
	return Object.entries(INDUSTRY_SLUG_MAP).find(
		([, value]) => value === industry,
	)?.[0];
};

// -----------------------------
// Showcase content for Shell & Tube Heat Exchangers
// -----------------------------
const STHE_IMAGES: string[] = [
	"/images/finalProducts/SIMPLEX AND DUPLEX HEAT EXCHANGER.png",
	"/images/finalProducts/DOUBLE SAFETY WITH LEKAGE DETETCTION WIRE.png",
];

// Icon order matches the highlight/spec keys resolved via i18n inside the component.
const STHE_HIGHLIGHT_ICONS = [ShieldCheck, Shapes, Diamond];
const STHE_SPEC_ICONS = [Gauge, Thermometer, Grid3x3, Layers, Workflow];

const ShellSubeHeatExchangers = () => {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// -----------------------------
	// âœ… NEW: State
	// -----------------------------
	const [activeIndustry, setActiveIndustry] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");

	// -----------------------------
	// âœ… NEW: Debounce Logic
	// -----------------------------
	useEffect(() => {
		const t = setTimeout(() => {
			setDebouncedSearch(search.toLowerCase());
		}, 300);
		return () => clearTimeout(t);
	}, [search]);

	// -----------------------------
	// âœ… NEW: Read URL param on load
	// -----------------------------
	useEffect(() => {
		const industrySlug = searchParams.get("industry");
		if (industrySlug && INDUSTRY_SLUG_MAP[industrySlug]) {
			setActiveIndustry(INDUSTRY_SLUG_MAP[industrySlug]);
		}
	}, []);

	// -----------------------------
	// Page Data (UNCHANGED)
	// -----------------------------
	const pageData = {
		title: t("productPages.shellTube.hero.title"),
		bgimage: "/images/header/SHELLANDTUBE.JPG",

		description: t("productPages.shellTube.hero.tagline"),

		meta: t("productPages.shellTube.hero.meta", {
			returnObjects: true,
		}) as string[],
		projects: [
			{
				id: "1",
				title: "Water cooled Oil Cooler 12 kW",
				application: "Sea water cooled Oil Cooler",
				capacity: "12 kW",
				image: "/images/products3D/indian navy.png",
				details: {
					"Applicable Industry": "Hydraulics",
					"Tube MOC": "Cu Ni 70:30",
					"Tube sheet MOC": "AB2",
					"Shell & End Covers": "Gun Metal",
					Materials: "As per NES (Naval Engineering Standard)",
					"Inspection Agencies": "CQAE, DGQA",
				},
				aplication: ["Marine & Offshore", "Water Treatment"],
			},
			{
				id: "2",
				title: "Water Heater 20228 kW",
				application: "Low Pressure Boiler Feed Water Heater",
				capacity: "20228 kW",
				image: "/images/products3D/CMPC.png",
				details: {
					"Applicable Industry": "Pulp and Paper",
					Certification: "CE-PED with Module G",
					"Installation Location": "Chile, South America",
					"Shell Diameter": "40 Inch",
					"Overall Length": "6 Mtr",
					"Steam Flow": "32481 kg/hr",
					"Boiler Feed Water Flow": "446000 kg/hr",
					"TEMA CLASS": "NEN",
					"Empty Weight of Heat Exchanger": "10000 Kg",
				},
				aplication: ["Water Treatment", "Industrial Heating"],
			},
			{
				id: "3",
				title: "Fertilizer Processing Heat Exchanger 1200 kW",
				application: "Industrial Heat Exchanger for Fertilizer Processing",
				capacity: "1200 kW",
				image: "/images/products3D/Lowa Fertilizer.png",
				details: {
					Industry: "Fertiliser",
					Certification: "ASME U Stamp + NB Stamp",
					"Installation Location": "United States of America",
					"Shell Diameter": "22 Inch",
					"Overall Length": "5.6 Mtr",
					"Tube MOC": "SA 249 TP 304L",
					"Shell MOC": "SA 240 TP 304L",
					"Weight of Heat Exchanger": "5500 Kg",
				},
				aplication: ["Chemical Processing", "Industrial Heating"],
			},
			{
				id: "4",
				title: "Water Heat Exchanger 2000 kW",
				application: "Boiler Feed Water Heat Exchanger",
				capacity: "2000 kW",
				image: "/images/products3D/NTPC.png",
				details: {
					Industry: "Power",
					"Shell Diameter": "38 Inch",
					"Overall Height": "4.2 Mtr",
					"Tube MOC": "SA 249 TP 304",
					"Shell MOC": "SA 516 Gr. 70",
					Construction: "TEMA C - BFW",
					"Weight of Heat Exchanger": "15000 Kg",
				},
				aplication: ["Power Generation", "Industrial Heating"],
			},
			{
				id: "5",
				title: "Heat Exchanger 1080 kW",
				application: "Power Plant Heat Exchanger",
				capacity: "1080 kW",
				image: "/images/products3D/Doosan.png",
				details: {
					"Applicable Industry": "Power Industry",
					Certification: "ASME U Stamp + NB Stamp",
					"Shell Diameter": "24 Inch",
					"Overall Length": "5.4 Mtr",
					"Tube MOC": "SA 213 TP 304",
					"Shell MOC": "SA 516 Gr. 70",
					"Weight of Heat Exchanger": "8000 Kg",
				},
				aplication: ["Power Generation", "Thermal Systems"],
			},
			{
				id: "6",
				title: "Heat Exchanger  2800 kW",
				application: "Oil & Gas Process Heat Exchanger",
				capacity: "2800 kW",
				image: "/images/products3D/ONGC.png",
				details: {
					"Applicable Industry": "Oil & Gas",
					"Special Feature":
						"Duplex Shell & Tube Heat Exchanger with Temperature Control Valve",
					"Equipment Type": "Shell & Tube Heat Exchanger",
					"Feature Detail":
						"Integrated Temperature Control Valve for enhanced process regulation",
				},
				aplication: ["Oil & Gas Processing", "Thermal Systems"],
			},
		],
	};

	// -----------------------------
	// âœ… NEW: Filter + Search + Sort
	// -----------------------------
	const filteredProjects = useMemo(() => {
		let data = [...pageData.projects];

		// SEARCH
		if (debouncedSearch) {
			data = data.filter((item) => {
				const text = (
					item.title +
					" " +
					(item.application || "")
				).toLowerCase();

				const industries = (item.aplication || []).join(" ").toLowerCase();

				return (
					text.includes(debouncedSearch) || industries.includes(debouncedSearch)
				);
			});
		}

		// FILTER
		if (activeIndustry) {
			data = data.filter((item) =>
				(item.aplication || []).includes(activeIndustry),
			);
		}

		// SORT
		if (activeIndustry) {
			data.sort((a, b) => {
				const aMatch = (a.aplication || []).includes(activeIndustry) ? 1 : 0;
				const bMatch = (b.aplication || []).includes(activeIndustry) ? 1 : 0;
				return bMatch - aMatch;
			});
		}

		return data;
	}, [debouncedSearch, activeIndustry]);

	// -----------------------------
	// âœ… NEW: Handle Filter Click
	// -----------------------------
	const handleFilterClick = (industry: string) => {
		const isSame = activeIndustry === industry;

		if (isSame) {
			setActiveIndustry(null);
			setSearchParams({});
		} else {
			setActiveIndustry(industry);
			const slug = getSlugFromIndustry(industry);
			setSearchParams({ industry: slug || "" });
		}
	};

	// Localized showcase content (icons stay static; copy resolves via i18n).
	const highlightKeys = [
		"designStandards",
		"customGeometry",
		"materialIntegrity",
	] as const;
	const specKeys = [
		"designPressure",
		"temperatureRange",
		"tubePatterns",
		"designType",
		"arrangement",
	] as const;

	const STHE_HIGHLIGHTS: ShowcaseHighlight[] = highlightKeys.map((k, i) => ({
		icon: STHE_HIGHLIGHT_ICONS[i],
		title: t(`productPages.shellTube.showcase.highlights.${k}.title`),
		body: t(`productPages.shellTube.showcase.highlights.${k}.body`),
	}));

	const STHE_SPECS: ShowcaseSpec[] = specKeys.map((k, i) => ({
		icon: STHE_SPEC_ICONS[i],
		feature: t(`productPages.shellTube.showcase.specs.${k}.feature`),
		capability: t(`productPages.shellTube.showcase.specs.${k}.capability`),
	}));

	return (
		<>
			<Seo
				titleKey="productPages.shellTube.seo.title"
				descriptionKey="productPages.shellTube.seo.description"
				path="/products/shell-tube-heat-exchangers"
			/>
			<Navigation />

			{/* HERO */}
			<SectionWrapper className={PAGE_HERO_CLASS}>
				<div className="absolute inset-0 ">
					<div
						className="absolute bottom-3 inset-0 opacity-35 scale-105"
						style={{
							backgroundImage: `url(${pageData.bgimage})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					/>
					<DotGrid
						dotSize={4}
						gap={15}
						baseColor="#3b0092"
						activeColor="#f09a38"
					/>
				</div>

				<PageHeaderHero
					title={pageData.title}
					description={pageData.description}
					meta={pageData.meta}
				/>
			</SectionWrapper>

			{/* -----------------------------
			    âœ… NEW: FILTER + SEARCH UI
			------------------------------ */}
			{/* <div className="max-w-7xl mt-10 mx-auto px-4 sm:px-6 lg:px-12 flex  items-center justify-center">
				
				<input
					type="text"
					placeholder="Search products..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="w-full mb-6 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#3b0092]"
				/>

				<div className="flex 	flex-wrap items-center justify-center gap-3 pb-3">
						<button
							onClick={() => {
								setActiveIndustry(null);
								setSearchParams({});
							}}
							className={`px-4 py-2 rounded-full border text-sm transition-all ${
								!activeIndustry ?
									"bg-[#3b0092] text-white border-[#3b0092]"
								:	"bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
							}`}>
							All
						</button>

						{INDUSTRIES.map((industry) => {
							const isActive = activeIndustry === industry;

							return (
								<button
									key={industry}
									onClick={() => handleFilterClick(industry)}
									className={`whitespace-nowrap px-4 py-2 rounded-full border transition-all text-sm ${
										isActive ?
											"bg-[#3b0092] text-white border-[#3b0092]"
										:	"bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
									}`}>
									{industry}
								</button>
							);
						})}
					</div>
			</div> */}

			{/* PRODUCTS */}
			{/* <SectionWrapper className="pt-0">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
						{filteredProjects.map((project, index) => (
							<RevealOnScroll
								key={project.id}
								delay={index * 0.1}>
								<ProductCard
									product={project}
									index={index}
								/>
							</RevealOnScroll>
						))}
					</div>

					{filteredProjects.length === 0 && (
						<p className="text-center text-gray-500 mt-10">
							No matching projects found.
						</p>
					)}
				</div>
			</SectionWrapper> */}

			<AcheShowcase
				title={
					<>
						{t("productPages.shellTube.showcase.titleMain")}{" "}
						<span
							className="italic font-medium"
							style={{ color: "#3b0092" }}>
							{t("productPages.shellTube.showcase.titleAccent")}
						</span>
					</>
				}
				description={(() => {
					const emphasis = t(
						"productPages.shellTube.showcase.descriptionEmphasis",
					);
					const parts = t("productPages.shellTube.showcase.description").split(
						emphasis,
					);
					if (parts.length < 2) {
						return <>{t("productPages.shellTube.showcase.description")}</>;
					}
					return (
						<>
							{parts[0]}
							<span
								className="font-semibold"
								style={{ color: "#3b0092" }}>
								{emphasis}
							</span>
							{parts.slice(1).join(emphasis)}
						</>
					);
				})()}
				images={STHE_IMAGES}
				imageAlt={t("productPages.shellTube.showcase.imageAlt")}
				wordmarkPrimary="STHE"
				wordmarkSecondary="POSITRON"
				compliancePill="ASME VIII Â· TEMA R/C/B"
				captionEyebrow="Process Thermal Solution"
				captionTitle="Shell & Tube Heat Exchanger"
				eyebrow={t("productPages.shellTube.showcase.eyebrow")}
				specsTitle={t("productPages.shellTube.showcase.specsTitle")}
				specsEyebrow={t("productPages.shellTube.showcase.specsEyebrow")}
				highlights={STHE_HIGHLIGHTS}
				specs={STHE_SPECS}
				specsFooterLabel="Positron Engineering Â· STHE Series"
			/>

			<FooterSection />
		</>
	);
};

export default ShellSubeHeatExchangers;
